import React,{ useEffect, useRef } from 'react';

import {
  Avatar,
  CakeIcon,
  WorkIcon,
  getCelebrationEvents,
  getDaysUntil,
  getNumberSuffix,
  CelebrationWheel,
  CelebrationInformation,
} from 'src/utils/celebrationUtils'; 

const formatCelebrationDate = (date: Date | string) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  }).toUpperCase();
};

const CelebrationItemList = ({ data, period}: { data: CelebrationInformation[]; period: 'week' | 'three-weeks';}) => {

  const flatSortedEvents = data.flatMap((celebration) =>
      getCelebrationEvents(celebration).map((event) => ({
        ...event,
        employeeId: celebration.employeeId,
        employeeName: celebration.employeeName,
        anniversaryCount: celebration.anniversaryCount,
        milestone: celebration.milestone,
        birthday: celebration.birthday
      }))
    )
    .sort((a, b) => {
      const mmddA = a.date.slice(5);
      const mmddB = b.date.slice(5);
      return mmddA.localeCompare(mmddB);
    });

  const isWheelActive = flatSortedEvents.length > 3;
  const now = new Date();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
  const startStr = startOfWeek.toISOString().slice(5, 10);


  const endOfWeek = new Date(now);
  endOfWeek.setDate(now.getDate() + (6 - dayOfWeek));
  const endStr = endOfWeek.toISOString().slice(5, 10);

  const getCategory = (date: Date) => {
    const dateStr = new Date(date).toISOString().slice(5, 10);
    if (dateStr < startStr) return 'past';
    if (dateStr >= startStr && dateStr <= endStr) return 'current';
    return 'future';
  };

  // Find index of closest upcoming or today event
  const closestIndex = flatSortedEvents.findIndex((event) => getDaysUntil(event.date) >= 0); 


  // If none upcoming, focus last event (past events)
  const focusIndex = closestIndex === -1 ? 0 : closestIndex;

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

useEffect(() => {
  if (period !== 'three-weeks') return;

  const container = containerRef.current;
  const item = itemRefs.current[focusIndex];

  if (container && item) {
    // Get container's visible vertical boundaries
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.clientHeight;

    // Get item's position within container
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.clientHeight;

    // Check if item is outside visible area
    const isOutOfView = itemTop < containerTop || itemBottom > containerBottom;

    // Base offset for spacing above the item when scrolling
    let offset = 126;

    if (isOutOfView) {
      container.scrollTo({
        top: item.offsetTop - offset,
        behavior: "smooth",
      });
    }
  }
}, [focusIndex, flatSortedEvents]);

  let lastCategory = '';
  return (
    <div ref={containerRef} style={{ maxHeight: "400px", overflowY: "auto" }} >
      {isWheelActive && period == 'week' ? (
        <CelebrationWheel
          events={flatSortedEvents}
        />
      ):(
        flatSortedEvents.map((event, index) => {
          const daysUntil = getDaysUntil(event.date);
          const category = getCategory(event.date);
          const showHeader = period === 'three-weeks' && category !== lastCategory;
          lastCategory = category;

          const bgColor = {
            past: '#b3c7f0',
            current: '#809ae0',
            future: '#6677c8',
          }[category];

          return (

            <React.Fragment key={`${event.employeeId}-${event.label}-${index}`}>
              {showHeader && (
                <div className='dialog-title-container' style={{ backgroundColor: bgColor}}>
                  <span className="dialog-title">
                    {category === 'past' && 'Past Week Celebrations'}
                    {category === 'current' && 'Current Week Celebrations'}
                    {category === 'future' && 'Future Week Celebrations'}
                  </span>
                </div>
              )}
              <div
                key={`${event.employeeId}-${event.label}-${index}`}
                className="celebrations-item"
                ref={(el) => (itemRefs.current[index] = el)}
              >

                <div>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 35, height: 35, marginLeft: '5px', marginRight: '5px' }}>
                    {event.employeeName
                      .split(" ")
                      .map(word => word.charAt(0).toUpperCase())
                      .join("")}
                  </Avatar>
                </div>

                <div className='celebration-content-column'>
                  <div className='celebration-header-row'>
                    <div>
                      <span className="emp-name">{event.employeeName}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <span className="celebrations-date">{formatCelebrationDate(event.date)}</span>
                      {event.birthday && event.icon === CakeIcon && daysUntil < 0 &&(
                        <span className="celebrations-name">{" "}A memory well made</span>
                      )}
                      {event.birthday && event.icon === CakeIcon && daysUntil == 0 &&(
                        <span className="celebrations-name">{" "}Time to celebrate</span>
                      )}
                      {event.birthday && event.icon === CakeIcon && daysUntil > 0 &&(
                        <span className="celebrations-name">{" "}A day to remember</span>
                      )}
                      {event.milestone && event.icon === WorkIcon && (
                        <span className="celebrations-name">{" "}{event.anniversaryCount}
                        {getNumberSuffix(event.anniversaryCount)} year completed</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="celebrations-icon" >
                  {daysUntil > 0 ? (
                    <span>
                      In {daysUntil} {daysUntil === 1 ? 'day' : 'days'}
                    </span>
                  ) : daysUntil === 0 ? (
                    <span>Today</span>
                  ) : (
                    <span>
                      {Math.abs(daysUntil)} {Math.abs(daysUntil) === 1 ? 'day' : 'days'} ago
                    </span>
                  )}

                  <event.icon sx={{ fontSize: '24px', marginRight: '4px', marginBottom: '4px', color: (event.color) }} />
                </div>

              </div>
            </React.Fragment>
          );
        })
      )}
      
    </div>

  );
};

export default CelebrationItemList;
